import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import i18n from '@/i18n';
import {
  getNotificationPermission,
  syncFcmSubscriptions,
  type NotificationPermission,
} from '@/libs/fcm-subscription';
import { useAppStore } from '@/stores/app';
import { useFavoriteGroupsStore } from '@/stores/favorite-groups';

const { t } = i18n.global;

export const useFavoriteNotificationsStore = defineStore('favorite-notifications', () => {
  const appStore = useAppStore();
  const favoriteGroupsStore = useFavoriteGroupsStore();

  const permission = ref<NotificationPermission>('granted');
  const isBlocked = computed(() => permission.value === 'denied');

  const topics = computed<string[]>(() =>
    favoriteGroupsStore.favorites
      .filter(favorite => favorite.notificationsEnabled)
      .map(favorite => `${import.meta.env.VITE_FCM_TOPIC_PREFIX || ''}${favorite.groupId}`),
  );

  // Serialized, and the topics read when a run starts: a sync queued behind a
  // failed toggle sees the switch already back where it was.
  let queue: Promise<boolean> = Promise.resolve(true);
  function sync(): Promise<boolean> {
    queue = queue.then(() => syncFcmSubscriptions(topics.value));
    return queue;
  }

  async function refreshPermission() {
    try {
      permission.value = await getNotificationPermission();
    } catch (error) {
      console.error('Notification permission check error:', error);
    }
  }

  async function setNotificationsEnabled(groupId: string, enabled: boolean) {
    favoriteGroupsStore.setNotificationsEnabled(groupId, enabled);
    if (await sync()) return;
    favoriteGroupsStore.setNotificationsEnabled(groupId, !enabled);
    appStore.showToast(t('favoriteGroups.notificationsError'));
  }

  // Covers additions, removals and a permission granted later; idempotent, so
  // the sync a toggle already ran makes this one a no-op.
  watch(
    () => `${permission.value}|${topics.value.join(',')}`,
    () => void sync(),
  );

  return { isBlocked, refreshPermission, setNotificationsEnabled, sync };
});
